import React, { useMemo } from 'react'
import Tab from 'Components/DesignSystem/Tab/Tab';
import { TabItem } from 'types/DesignSystem/Tab';

type TabGroupProps = {
    tabs: { label: string; isDisabled?: boolean }[];
    activeTabIndex: number;
    onTabClick: (index: number) => void;
}

/**
 * Step 2's three attribute tabs, on DS `Tab` since 2026-09-13.
 *
 * It was a local `Tab.tsx` drawing its own 197px underline on the legacy `#0747a6`,
 * inside a `justify-evenly` strip with a hand-set `top-[100px]` sticky offset. That
 * file had exactly one consumer — this one — and is deleted with it.
 *
 * **The caller's contract is unchanged.** `SelectAttributes` still owns
 * `activeTabIndex` and still receives an INDEX, so nothing above this file moved; the
 * label doubles as the DS tab `id` purely to translate between the two, and the
 * per-tab testids are the same strings the old markup emitted.
 *
 * One behavioural difference, unreachable today: the local `Tab` removed `onClick`
 * when `isDisabled` but left the button natively enabled (a focusable no-op it pinned
 * as must-not-regress). DS `Tab` sets `disabled` and `aria-disabled`. No production
 * caller passes `isDisabled` — `SelectAttributes` sends three plain labels.
 */
const TabGroup: React.FC<TabGroupProps> = ({ tabs, activeTabIndex, onTabClick }) => {
    const items = useMemo<TabItem[]>(() => tabs.map((tab) => ({
        id: tab.label,
        label: tab.label,
        disabled: tab.isDisabled,
        'data-testid': `recategorize-tab-${tab.label}`,
    })), [tabs]);

    return (
        <div className="tw-bg-surface tw-sticky tw-top-[100px] tw-z-10">
            <Tab
                tabs={items}
                activeTabId={tabs[activeTabIndex]?.label}
                onChange={(id) => {
                    const index = tabs.findIndex((t) => t.label === id);
                    if (index !== -1) onTabClick(index);
                }}
            />
        </div>
    );
};

export default TabGroup;
